import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/AdminLayout';
import CandidateCard from '../components/CandidateCard';
import JobRecommendationModal from '../components/JobRecommendationModal';
import DavidConversationsPanel from '../components/DavidConversationsPanel';
import { Search, Users, MessageCircle, RefreshCw, Download, ChevronLeft, ChevronRight, Code2 } from 'lucide-react';
import {
    CandidateForRecommendation,
    fetchApprovedCandidates,
    fetchImportedCandidates,
    getDavidConversations
} from '../services/recommendationsApi';

type SourceTab = 'platform' | 'imported' | 'imported_se';
type SubTab = 'all' | 'contacted';

const CandidateRecommendations: React.FC = () => {
    // Source tab state
    const [sourceTab, setSourceTab] = useState<SourceTab>('platform');

    // Platform candidates state
    const [platformCandidates, setPlatformCandidates] = useState<CandidateForRecommendation[]>([]);
    const [contactedCandidateIds, setContactedCandidateIds] = useState<Set<string>>(new Set());
    const [platformLoading, setPlatformLoading] = useState(true);
    const [platformError, setPlatformError] = useState<string | null>(null);
    const [platformSubTab, setPlatformSubTab] = useState<SubTab>('all');

    // Imported (all_roles) candidates state (server-side pagination)
    const [importedCandidates, setImportedCandidates] = useState<CandidateForRecommendation[]>([]);
    const [importedTotal, setImportedTotal] = useState(0);
    const [importedPage, setImportedPage] = useState(1);
    const [importedLimit] = useState(20);
    const [importedLoading, setImportedLoading] = useState(false);
    const [importedError, setImportedError] = useState<string | null>(null);
    const [importedSearchQuery, setImportedSearchQuery] = useState('');
    const [importedSearchDebounced, setImportedSearchDebounced] = useState('');

    // Imported · Software Engineering candidates state (server-side pagination).
    // Uses the same matches-vectors endpoint as the all_roles list, with
    // ?collection=software_engineering to dispatch to the SE sub-collection.
    const [importedSECandidates, setImportedSECandidates] = useState<CandidateForRecommendation[]>([]);
    const [importedSETotal, setImportedSETotal] = useState(0);
    const [importedSEPage, setImportedSEPage] = useState(1);
    const [importedSELoading, setImportedSELoading] = useState(false);
    const [importedSEError, setImportedSEError] = useState<string | null>(null);
    const [importedSESearchQuery, setImportedSESearchQuery] = useState('');
    const [importedSESearchDebounced, setImportedSESearchDebounced] = useState('');

    // Shared state
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCandidate, setSelectedCandidate] = useState<CandidateForRecommendation | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadingCandidateId] = useState<string | null>(null);

    // Load platform candidates on mount
    useEffect(() => {
        loadPlatformData();
    }, []);

    // Load imported candidates when tab, page, or search changes
    useEffect(() => {
        if (sourceTab === 'imported') {
            loadImportedData();
        }
    }, [sourceTab, importedPage, importedSearchDebounced]);

    // Debounce imported search
    useEffect(() => {
        const timer = setTimeout(() => {
            setImportedSearchDebounced(importedSearchQuery);
            setImportedPage(1); // Reset to page 1 on search
        }, 400);
        return () => clearTimeout(timer);
    }, [importedSearchQuery]);

    // Load Imported SE candidates when tab, page, or search changes
    useEffect(() => {
        if (sourceTab === 'imported_se') {
            loadImportedSEData();
        }
    }, [sourceTab, importedSEPage, importedSESearchDebounced]);

    // Debounce Imported SE search
    useEffect(() => {
        const timer = setTimeout(() => {
            setImportedSESearchDebounced(importedSESearchQuery);
            setImportedSEPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [importedSESearchQuery]);

    const loadPlatformData = async () => {
        setPlatformLoading(true);
        setPlatformError(null);
        try {
            const [candidatesData, conversationsData] = await Promise.all([
                fetchApprovedCandidates(),
                getDavidConversations().catch((err) => {
                    console.error('[RecentlyContacted] getDavidConversations failed:', err);
                    return [];
                })
            ]);

            const contactedIds = new Set(conversationsData.map(c => c.candidateId));
            setContactedCandidateIds(contactedIds);

            const candidatesWithContact = candidatesData.map(candidate => {
                const conversation = conversationsData.find(c => c.candidateId === candidate.id);
                return {
                    ...candidate,
                    source: 'platform' as const,
                    ...(conversation && {
                        lastContactedAt: conversation.updatedAt || conversation.createdAt,
                        lastMessage: conversation.lastMessage
                    })
                };
            });

            setPlatformCandidates(candidatesWithContact);
        } catch (err) {
            setPlatformError(err instanceof Error ? err.message : 'Failed to load candidates');
        } finally {
            setPlatformLoading(false);
        }
    };

    const loadImportedData = async () => {
        setImportedLoading(true);
        setImportedError(null);
        try {
            const data = await fetchImportedCandidates(importedPage, importedLimit, importedSearchDebounced, 'all_roles');
            setImportedCandidates(data.candidates);
            setImportedTotal(data.total);
        } catch (err) {
            setImportedError(err instanceof Error ? err.message : 'Failed to load imported candidates');
        } finally {
            setImportedLoading(false);
        }
    };

    const loadImportedSEData = async () => {
        setImportedSELoading(true);
        setImportedSEError(null);
        try {
            const data = await fetchImportedCandidates(importedSEPage, importedLimit, importedSESearchDebounced, 'software_engineering');
            setImportedSECandidates(data.candidates);
            setImportedSETotal(data.total);
        } catch (err) {
            setImportedSEError(err instanceof Error ? err.message : 'Failed to load imported SE candidates');
        } finally {
            setImportedSELoading(false);
        }
    };

    // Platform-side client filter
    const filteredPlatformCandidates = useMemo(() => {
        let filtered = platformCandidates;
        if (platformSubTab === 'contacted') {
            filtered = filtered.filter(c => contactedCandidateIds.has(c.id));
        }
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(c =>
                c.firstName?.toLowerCase().includes(query) ||
                c.lastName?.toLowerCase().includes(query) ||
                c.email?.toLowerCase().includes(query) ||
                c.currentTitle?.toLowerCase().includes(query) ||
                c.skills?.some(skill => skill.toLowerCase().includes(query))
            );
        }
        return filtered;
    }, [platformCandidates, platformSubTab, searchQuery, contactedCandidateIds]);

    const handleGetRecommendations = (candidate: CandidateForRecommendation) => {
        setSelectedCandidate(candidate);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedCandidate(null);
    };

    const handleSendSuccess = () => {
        if (sourceTab === 'platform') loadPlatformData();
    };

    const handleRefresh = () => {
        if (sourceTab === 'platform') loadPlatformData();
        else if (sourceTab === 'imported') loadImportedData();
        else loadImportedSEData();
    };

    const contactedCount = platformCandidates.filter(c => contactedCandidateIds.has(c.id)).length;
    const loading =
        sourceTab === 'platform' ? platformLoading
        : sourceTab === 'imported' ? importedLoading
        : importedSELoading;
    const error =
        sourceTab === 'platform' ? platformError
        : sourceTab === 'imported' ? importedError
        : importedSEError;
    const totalImportedPages = Math.ceil(importedTotal / importedLimit);
    const totalImportedSEPages = Math.ceil(importedSETotal / importedLimit);

    return (
        <AdminLayout
            showHeader={true}
            headerTitle="Candidate Recommendations"
            headerSubtitle="Send personalized job recommendations to candidates"
        >
            {/* Source Tabs: Platform | Imported */}
            <div className="flex items-center gap-2 mb-4">
                <button
                    onClick={() => setSourceTab('platform')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        sourceTab === 'platform'
                            ? 'bg-[#015FA6] text-white shadow-sm'
                            : 'bg-white text-[#757575] border border-[#EDEDED] hover:bg-[#F1F3F4]'
                    }`}
                >
                    <Users size={16} />
                    Platform Candidates
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                        sourceTab === 'platform' ? 'bg-white/20 text-white' : 'bg-[#F1F3F4] text-[#757575]'
                    }`}>
                        {platformCandidates.length}
                    </span>
                </button>

                <button
                    onClick={() => setSourceTab('imported')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        sourceTab === 'imported'
                            ? 'bg-[#015FA6] text-white shadow-sm'
                            : 'bg-white text-[#757575] border border-[#EDEDED] hover:bg-[#F1F3F4]'
                    }`}
                >
                    <Download size={16} />
                    Imported Candidates
                    {importedTotal > 0 && (
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                            sourceTab === 'imported' ? 'bg-white/20 text-white' : 'bg-[#F1F3F4] text-[#757575]'
                        }`}>
                            {importedTotal.toLocaleString()}
                        </span>
                    )}
                </button>

                <button
                    onClick={() => setSourceTab('imported_se')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        sourceTab === 'imported_se'
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'bg-white text-[#757575] border border-[#EDEDED] hover:bg-[#F1F3F4]'
                    }`}
                >
                    <Code2 size={16} />
                    Imported · Software Eng
                    {importedSETotal > 0 && (
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                            sourceTab === 'imported_se' ? 'bg-white/20 text-white' : 'bg-[#F1F3F4] text-[#757575]'
                        }`}>
                            {importedSETotal.toLocaleString()}
                        </span>
                    )}
                </button>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#EDEDED] min-h-[calc(100vh-230px)]">
                {/* Sub-tabs (platform only) + Search */}
                <div className="flex items-center justify-between border-b border-[#EDEDED] mb-6">
                    <div className="flex items-center gap-8">
                        {sourceTab === 'platform' ? (
                            <>
                                <button
                                    onClick={() => setPlatformSubTab('all')}
                                    className={`pb-4 text-sm font-semibold relative flex items-center gap-2 ${
                                        platformSubTab === 'all' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
                                    }`}
                                >
                                    <Users size={16} />
                                    All Candidates
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                                        platformSubTab === 'all' ? 'bg-[#015FA6]/10 text-[#015FA6]' : 'bg-[#F1F3F4] text-[#757575]'
                                    }`}>
                                        {platformCandidates.length}
                                    </span>
                                    {platformSubTab === 'all' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
                                </button>
                                <button
                                    onClick={() => setPlatformSubTab('contacted')}
                                    className={`pb-4 text-sm font-semibold relative flex items-center gap-2 ${
                                        platformSubTab === 'contacted' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
                                    }`}
                                >
                                    <MessageCircle size={16} />
                                    Recently Contacted
                                    {contactedCount > 0 && (
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                                            platformSubTab === 'contacted' ? 'bg-[#015FA6]/10 text-[#015FA6]' : 'bg-[#F1F3F4] text-[#757575]'
                                        }`}>
                                            {contactedCount}
                                        </span>
                                    )}
                                    {platformSubTab === 'contacted' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
                                </button>
                            </>
                        ) : sourceTab === 'imported' ? (
                            <div className="pb-4 text-sm font-semibold text-[#015FA6] flex items-center gap-2 relative">
                                <Download size={16} />
                                Imported Candidates
                                <span className="px-2 py-0.5 rounded-full text-xs bg-[#015FA6]/10 text-[#015FA6]">
                                    {importedTotal.toLocaleString()}
                                </span>
                                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />
                            </div>
                        ) : (
                            <div className="pb-4 text-sm font-semibold text-purple-600 flex items-center gap-2 relative">
                                <Code2 size={16} />
                                Imported · Software Engineering
                                <span className="px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-600">
                                    {importedSETotal.toLocaleString()}
                                </span>
                                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-purple-600 rounded-t-full" />
                            </div>
                        )}
                    </div>

                    {/* Search and Refresh (hidden in contacted view) */}
                    {!(sourceTab === 'platform' && platformSubTab === 'contacted') && (
                        <div className="flex items-center gap-3 pb-4">
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#757575]" />
                                <input
                                    type="text"
                                    placeholder={
                                        sourceTab === 'imported' || sourceTab === 'imported_se'
                                            ? 'Search by name, email, skills...'
                                            : 'Search candidates...'
                                    }
                                    value={
                                        sourceTab === 'imported'
                                            ? importedSearchQuery
                                            : sourceTab === 'imported_se'
                                                ? importedSESearchQuery
                                                : searchQuery
                                    }
                                    onChange={e => {
                                        if (sourceTab === 'imported') setImportedSearchQuery(e.target.value);
                                        else if (sourceTab === 'imported_se') setImportedSESearchQuery(e.target.value);
                                        else setSearchQuery(e.target.value);
                                    }}
                                    className="pl-9 pr-4 py-2 w-72 rounded-lg border border-[#EDEDED] text-sm focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                            </div>
                            <button
                                onClick={handleRefresh}
                                disabled={loading}
                                className="p-2 rounded-lg border border-[#EDEDED] hover:bg-[#F1F3F4] disabled:opacity-50 transition-colors"
                                title="Refresh"
                            >
                                <RefreshCw size={16} className={`text-[#757575] ${loading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-12 h-12 border-3 border-[#015FA6]/20 border-t-[#015FA6] rounded-full animate-spin mb-4" />
                        <p className="text-sm text-[#757575]">Loading candidates...</p>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <p className="text-red-500 text-base mb-4">{error}</p>
                        <button onClick={handleRefresh} className="px-6 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a85]">
                            Retry
                        </button>
                    </div>
                ) : sourceTab === 'platform' ? (
                    platformSubTab === 'contacted' ? (
                        // Messaging UI for contacted candidates
                        <DavidConversationsPanel candidates={platformCandidates} />
                    ) : filteredPlatformCandidates.length === 0 ? (
                        <div className="flex flex-col items-center justify-center min-h-[400px]">
                            <Users className="w-24 h-24 mb-6 text-[#CBCBCB]" strokeWidth={1} />
                            <p className="text-[#A5A5A5] text-sm font-medium">
                                {searchQuery
                                    ? 'No candidates match your search'
                                    : 'No approved candidates found'}
                            </p>
                            {searchQuery && (
                                <button onClick={() => setSearchQuery('')} className="mt-4 text-sm text-[#015FA6] hover:underline">
                                    Clear search
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredPlatformCandidates.map(candidate => (
                                <CandidateCard
                                    key={candidate.id}
                                    candidate={candidate}
                                    onGetRecommendations={handleGetRecommendations}
                                    isLoading={loadingCandidateId === candidate.id}
                                />
                            ))}
                        </div>
                    )
                ) : sourceTab === 'imported' ? (
                    // Imported (all_roles) candidates list with pagination
                    importedCandidates.length === 0 ? (
                        <div className="flex flex-col items-center justify-center min-h-[400px]">
                            <Download className="w-24 h-24 mb-6 text-[#CBCBCB]" strokeWidth={1} />
                            <p className="text-[#A5A5A5] text-sm font-medium">
                                {importedSearchQuery ? 'No imported candidates match your search' : 'No imported candidates found'}
                            </p>
                            {importedSearchQuery && (
                                <button onClick={() => setImportedSearchQuery('')} className="mt-4 text-sm text-[#015FA6] hover:underline">
                                    Clear search
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3">
                                {importedCandidates.map(candidate => (
                                    <CandidateCard
                                        key={candidate.id}
                                        candidate={candidate}
                                        onGetRecommendations={handleGetRecommendations}
                                        isLoading={loadingCandidateId === candidate.id}
                                    />
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalImportedPages > 1 && (
                                <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#EDEDED]">
                                    <p className="text-sm text-[#757575]">
                                        Showing {((importedPage - 1) * importedLimit) + 1}–{Math.min(importedPage * importedLimit, importedTotal)} of {importedTotal.toLocaleString()}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setImportedPage(p => Math.max(1, p - 1))}
                                            disabled={importedPage <= 1}
                                            className="p-2 rounded-lg border border-[#EDEDED] hover:bg-[#F1F3F4] disabled:opacity-30 transition-colors"
                                        >
                                            <ChevronLeft size={16} className="text-[#757575]" />
                                        </button>
                                        <span className="text-sm text-[#515151] px-3">
                                            Page {importedPage} of {totalImportedPages}
                                        </span>
                                        <button
                                            onClick={() => setImportedPage(p => Math.min(totalImportedPages, p + 1))}
                                            disabled={importedPage >= totalImportedPages}
                                            className="p-2 rounded-lg border border-[#EDEDED] hover:bg-[#F1F3F4] disabled:opacity-30 transition-colors"
                                        >
                                            <ChevronRight size={16} className="text-[#757575]" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )
                ) : (
                    // Imported · Software Engineering candidates list with pagination
                    importedSECandidates.length === 0 ? (
                        <div className="flex flex-col items-center justify-center min-h-[400px]">
                            <Code2 className="w-24 h-24 mb-6 text-[#CBCBCB]" strokeWidth={1} />
                            <p className="text-[#A5A5A5] text-sm font-medium">
                                {importedSESearchQuery
                                    ? 'No imported SE candidates match your search'
                                    : 'No imported SE candidates found'}
                            </p>
                            {importedSESearchQuery && (
                                <button onClick={() => setImportedSESearchQuery('')} className="mt-4 text-sm text-purple-600 hover:underline">
                                    Clear search
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3">
                                {importedSECandidates.map(candidate => (
                                    <CandidateCard
                                        key={candidate.id}
                                        candidate={candidate}
                                        onGetRecommendations={handleGetRecommendations}
                                        isLoading={loadingCandidateId === candidate.id}
                                    />
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalImportedSEPages > 1 && (
                                <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#EDEDED]">
                                    <p className="text-sm text-[#757575]">
                                        Showing {((importedSEPage - 1) * importedLimit) + 1}–{Math.min(importedSEPage * importedLimit, importedSETotal)} of {importedSETotal.toLocaleString()}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setImportedSEPage(p => Math.max(1, p - 1))}
                                            disabled={importedSEPage <= 1}
                                            className="p-2 rounded-lg border border-[#EDEDED] hover:bg-[#F1F3F4] disabled:opacity-30 transition-colors"
                                        >
                                            <ChevronLeft size={16} className="text-[#757575]" />
                                        </button>
                                        <span className="text-sm text-[#515151] px-3">
                                            Page {importedSEPage} of {totalImportedSEPages}
                                        </span>
                                        <button
                                            onClick={() => setImportedSEPage(p => Math.min(totalImportedSEPages, p + 1))}
                                            disabled={importedSEPage >= totalImportedSEPages}
                                            className="p-2 rounded-lg border border-[#EDEDED] hover:bg-[#F1F3F4] disabled:opacity-30 transition-colors"
                                        >
                                            <ChevronRight size={16} className="text-[#757575]" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )
                )}
            </div>

            {/* Job Recommendation Modal */}
            <JobRecommendationModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                candidate={selectedCandidate}
                onSuccess={handleSendSuccess}
            />
        </AdminLayout>
    );
};

export default CandidateRecommendations;
